import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Product } from '../types/product';
import { createTemplateFromProduct } from '../utils/templates/productTemplates';
import type { ProductTemplate } from '../utils/templates/productTemplates';
import { saveTemplate, getTemplates, deleteTemplate } from '../utils/templates/templateStorage';

interface Folder {
  id: string;
  name: string;
  order?: number;
}

interface TemplateState {
  templates: ProductTemplate[];
  activeTemplateId: string | null;
  isLoading: boolean;
  folders: Folder[];
  loadTemplates: () => Promise<void>;
  addTemplate: (name: string, product: Product, folderId: string) => Promise<ProductTemplate>;
  updateTemplate: (id: string, updates: Partial<ProductTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  setActiveTemplate: (id: string | null) => void;
  getTemplateById: (id: string) => ProductTemplate | undefined;
  replaceTemplates: (templates: ProductTemplate[]) => void;
  replaceFolders: (folders: Folder[]) => void;
  addFolder: (id: string, name: string) => void;
  updateFolder: (id: string, name: string) => void;
  deleteFolder: (id: string) => void;
  reorderFolders: (fromIndex: number, toIndex: number) => void;
  moveFolderItem: (templateId: string, targetFolderId: string) => void;
}

export const useTemplateStore = create<TemplateState>()(
  persist(
    (set, get) => ({
      templates: [],
      activeTemplateId: null,
      isLoading: false,
      folders: [{ id: 'default', name: 'お気に入り', order: 0 }],

      loadTemplates: async () => {
        set({ isLoading: true });
        try {
          const templates = await getTemplates();
          console.log('Loaded templates from Supabase:', templates);
          set({ templates, isLoading: false });
        } catch (error) {
          console.error('Failed to load templates:', error);
          set({ isLoading: false });
        }
      },

      addTemplate: async (name, product, folderId) => {
        const template = createTemplateFromProduct(product, name, folderId);
        
        // Set order to be at the end of templates in this folder
        const folderTemplates = get().templates.filter(t => t.folderId === folderId);
        template.order = folderTemplates.length;
        
        try {
          // Save to Supabase first
          await saveTemplate(template);
          
          set(state => ({
            templates: [template, ...state.templates],
            activeTemplateId: template.id
          }));
          return template;
        } catch (error) {
          console.error('Failed to add template:', error);
          throw error;
        }
      },

      updateTemplate: async (id, updates) => {
        const template = get().templates.find(t => t.id === id);
        if (!template) return;

        const updatedTemplate = {
          ...template,
          ...updates,
          updatedAt: new Date().toISOString()
        };

        try {
          await saveTemplate(updatedTemplate);
          set(state => ({
            templates: state.templates.map(t =>
              t.id === id ? updatedTemplate : t
            )
          }));
        } catch (error) {
          console.error('Failed to update template:', error);
          throw error;
        }
      },

      deleteTemplate: async (id) => {
        try {
          await deleteTemplate(id);
          set(state => ({
            templates: state.templates.filter(t => t.id !== id),
            activeTemplateId: state.activeTemplateId === id ? null : state.activeTemplateId
          }));
        } catch (error) {
          console.error('Failed to delete template:', error);
          throw error;
        }
      },

      setActiveTemplate: (id) => {
        set({ activeTemplateId: id });
      },

      getTemplateById: (id) => {
        return get().templates.find(t => t.id === id);
      },

      replaceTemplates: (templates) => {
        set({ 
          templates: templates.filter(t => !t.isDeleted),
          activeTemplateId: null
        });
      },

      replaceFolders: (folders) => {
        // Ensure default folder exists
        if (!folders.some(f => f.id === 'default')) {
          folders.unshift({ id: 'default', name: 'お気に入り', order: 0 });
        }
        
        // Make sure all folders have an order property
        const foldersWithOrder = folders.map((folder, index) => ({
          ...folder,
          order: folder.order !== undefined ? folder.order : index
        }));
        
        // Sort by order
        const sortedFolders = [...foldersWithOrder].sort((a, b) => 
          (a.order || 0) - (b.order || 0)
        );
        
        set({ folders: sortedFolders });
      },

      addFolder: (id, name) => {
        set(state => {
          const newFolder = { 
            id, 
            name,
            order: state.folders.length // Set order to be at the end
          };
          return {
            folders: [...state.folders, newFolder]
          };
        });
      },

      updateFolder: (id, name) => {
        set(state => ({
          folders: state.folders.map(folder =>
            folder.id === id ? { ...folder, name } : folder
          )
        }));
      },

      deleteFolder: (id) => {
        if (id === 'default') return; // Prevent deletion of default folder
        
        set(state => ({
          folders: state.folders.filter(folder => folder.id !== id),
          templates: state.templates.map(template =>
            template.folderId === id
              ? { ...template, folderId: 'default' }
              : template
          )
        }));
      },
      
      reorderFolders: (fromIndex, toIndex) => {
        set(state => {
          // Skip default folder which should always be first
          const defaultFolder = state.folders.find(f => f.id === 'default');
          const otherFolders = state.folders.filter(f => f.id !== 'default');
          
          // Adjust indices to account for default folder
          const adjustedFromIndex = fromIndex - (defaultFolder ? 1 : 0);
          const adjustedToIndex = toIndex - (defaultFolder ? 1 : 0);
          
          if (adjustedFromIndex < 0 || adjustedToIndex < 0 || 
              adjustedFromIndex >= otherFolders.length || 
              adjustedToIndex >= otherFolders.length) {
            return state;
          }
          
          // Reorder the non-default folders
          const newFolders = [...otherFolders];
          const [movedFolder] = newFolders.splice(adjustedFromIndex, 1);
          newFolders.splice(adjustedToIndex, 0, movedFolder);
          
          // Update order property
          const updatedFolders = newFolders.map((folder, index) => ({
            ...folder,
            order: index + 1 // Start from 1 since default is 0
          }));
          
          // Combine default folder with reordered folders
          const finalFolders = defaultFolder 
            ? [defaultFolder, ...updatedFolders] 
            : updatedFolders;
          
          return { folders: finalFolders };
        });
      },
      
      moveFolderItem: (templateId, targetFolderId) => {
        const template = get().templates.find(t => t.id === templateId);
        if (!template || template.folderId === targetFolderId) return;
        
        // Count templates in target folder to determine new order
        const targetFolderTemplates = get().templates.filter(t => t.folderId === targetFolderId);
        const newOrder = targetFolderTemplates.length;
        
        set(state => ({
          templates: state.templates.map(t => 
            t.id === templateId 
              ? { 
                  ...t, 
                  folderId: targetFolderId,
                  order: newOrder,
                  updatedAt: new Date().toISOString()
                } 
              : t
          )
        }));
        
        // Save the updated template
        const updatedTemplate = {
          ...template,
          folderId: targetFolderId,
          order: newOrder,
          updatedAt: new Date().toISOString()
        };
        
        saveTemplate(updatedTemplate).catch(error => {
          console.error('Failed to save template after moving:', error);
        });
      }
    }),
    {
      name: 'template-storage',
      partialize: (state) => ({
        templates: state.templates,
        folders: state.folders.filter(f => f.id !== 'default' || f.id === 'default' && f.name === 'お気に入り'),
        activeTemplateId: state.activeTemplateId
      })
    }
  )
);